declare const window: any;
declare const RFE: any;

/**
 * Class representing a RFE module that can be initialized and deinitialized
 */
export abstract class RFEModule {
    /**
     * @property isRFEModule - determines if the module has new structure (rewritten in .TS) or not (old modules in .JS)
     */
    static isRFEModule: boolean = true;

    /**
     * Abstract initialization method for the module
     * @param context - which element we want the module to be initialized for
     */
    abstract init(context: HTMLElement): void;

    /**
     * Abstract de-initialization method for the module
     * @param context - which element we want the module to be deinitialized for
     */
    abstract deinit(context: HTMLElement): void;
}

/**
 * Class representing a RFE module that is used for manipulating with Ui
 * It should be used as a base class for most of the modules that works with Ui components (Ui component is then
 * represented by RFEModuleUiInstance class)
 */
export abstract class RFEModuleUi extends RFEModule {
    /**
     * @prop instances - array of Ui components (RFEModuleUiInstance class)
     */
    public instances: Array<RFEModuleUiInstance> = [];

    /**
     * Initialization method for the module
     * @param context - Parent element for searching target elements (HTMLElement)
     */
    init(context: HTMLElement): void {
        const elements = this.getUiElements(context); // gets DOM elements

        for (let i = 0; i < elements.length; i++) {
            const el = elements[i];
            // checks if the instance for this element exists
            if (el instanceof HTMLElement && !this.findInstanceByElement(el)) { // ...if NOT, create new one
                const instance = this.createUiInstance(el);
                const initialized = instance.init();
                if (initialized) { // save an instance only if init is OK
                    this.instances.push(instance);
                }
            }
        }
    }

    /**
     * De-initialization method for the module
     * @param context - Parent element for searching target elements (HTMLElement)
     */
    deinit(context: HTMLElement): void {
        for (let i = this.instances.length - 1; i >= 0; i--) {
            const instance = this.instances[i];
            // deinit instances inside of context element only
            if (instance.el === context || RFE.isChildOf(instance.el, context)) {
                instance.deinit();
                delete this.instances[i]; // delete reference
                this.instances.splice(i, 1); // remove an item from array of instances
            }
        }
    }

    /**
     * Returns components (instance of the module) that are inside of selected DOM object
     * @param context - DOM object for what we need to find related instances (HTMLElement)
     * @returns array of module instances (RFEModuleUiInstance)
     */
    public findInstancesByContext(context: HTMLElement): Array<RFEModuleUiInstance> {
        return this.instances.filter((instance) => RFE.isChildOf(instance.el, context));
    }

    /**
     * Abstract method that returns the list of HTML elements in the provided context that are managed by that module
     * @param context - Root HTML element
     * @returns list of HTML elements in the provided context
     */
    abstract getUiElements(context: HTMLElement): NodeList | Array<HTMLElement>;

    /**
     * Factory method for the single component represented by the RFEModuleUiInstance class
     * @param el - root element for the component
     * @returns instance of the RFEModuleUiInstance or its parent
     */
    abstract createUiInstance(el: HTMLElement): RFEModuleUiInstance;

    /**
     * Returns component (instance of the module) related to selected DOM object
     * @param el - DOM object for what we need to find related instance (HTMLElement)
     * @returns module instance (RFEModuleUiInstance) or nothing (undefined)
     */
    public findInstanceByElement(el: HTMLElement): RFEModuleUiInstance | undefined {
        return this.instances.find((instance) => instance.el === el);
    }
}

/**
 * Abstract class that represents Ui component. Each module should have own Ui class which is extended from
 * RFEModuleUiInstance and contains at least init() and deinit() methods.
 */
export abstract class RFEModuleUiInstance {
    /** @prop module - reference to the module component */
    public module: RFEModule;

    /** @prop el - reference to the root element of the component */
    public el: HTMLElement;

    /**
     * Constructor method creates basic references
     */
    constructor(module: RFEModule, el: HTMLElement) {
        this.module = module;
        this.el = el;
    }

    /**
     * Initialization of single component
     * @returns TRUE/FALSE whether the initialization was successful or not
     */
    abstract init(): boolean;

    /**
     * De-initialization of single instance of the module
     */
    abstract deinit(): void;
}


/**
 * Class representing a RFE module that is used for global modules like API references
 * They have just single global instance, not related to DOM elements. It's initGlobal method is called just once.
 * WHen the module is to be initialized in the different context, it's initContext method is called
 */
export abstract class RFEGlobalModule extends RFEModule {
    /** @prop initialized - was the module already initialized? */
    public initialized: boolean = false;

    /**
     * Initialization method for the module
     * @param context - Parent element for searching target elements (HTMLElement)
     */
    init(context: HTMLElement): void {
        if (!this.initialized) {
            this.initialized = this.initGlobal();
        }
        if (this.initialized) {
            this.initContext(context);
        }
    }

    /**
     * De-initialization method for the module
     * @param context - Parent element for searching target elements
     */
    deinit(context: HTMLElement): void {
        if (this.initialized) {
            this.deinitContext(context);

            // main de-init will be processed only for default <body> context
            if (context === window.defaultLoaderContext) {
                this.deinitGlobal();
                this.initialized = false;
            }
        }
    }

    /**
     * Main initialization of module
     * @returns TRUE/FALSE if the initialization was successful or not
     */
    abstract initGlobal(): boolean;

    /**
     * Main de-initialization of module
     */
    abstract deinitGlobal(): void;

    /**
     * Optional lifecycle method for the re-initialization of the module in the different context
     * @param context - Parent element for searching target elements
     */
    initContext(context: HTMLElement): void {
        // can be implemented in the child class, by default it does nothing
    }

    /**
     * Optional lifecycle method for the de-initialization of the module in the different context
     * @param context - Parent element for searching target elements
     */
    deinitContext(context: HTMLElement): void {
        // can be implemented in the child class, by default it does nothing
    }
}



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./source/js/helpers/module.ts